import { FILTER_TYPE_SELECT } from '@/actionBoards/actionBoard.constants';
import type { DrillStep } from '@/actionBoards/actionBoard.types';
import type { FilterValue } from '@/serverSideTable/materializedTables/materializedTables.constants';
import type { BoardOutputV1 } from '@seeqdev/api/model/BoardOutputV1';
import type { DrillDimensionLevelOutputV1 } from '@seeqdev/api/model/DrillDimensionLevelOutputV1';

export interface HydratedDrillLevel {
  id: string;
  name: string;
  rank: number;
  columnName: string | null;
}

/** `assignedLevels` is the subset of `levels` that maps to a Calculated Table column on this board. */
export interface HydratedDrillDimension {
  id: string;
  name: string;
  levels: HydratedDrillLevel[];
  assignedLevels: HydratedDrillLevel[];
}

export type TileBreadcrumb = {
  dimension: HydratedDrillDimension;
  stack: DrillStep[];
};

export const sortLevelsByRank = (levels: DrillDimensionLevelOutputV1[] | undefined): DrillDimensionLevelOutputV1[] =>
  [...(levels ?? [])].sort((firstLevel, secondLevel) => (firstLevel.rank ?? 0) - (secondLevel.rank ?? 0));

/** Lower-case drill IDs so they match: the V1 serializer upper-cases UUID fields while the
 *  drillAssignments map keys arrive lowercase. */
export const normalizeBoardDrillIds = (board: BoardOutputV1): BoardOutputV1 => {
  const hasDrillData = board.drillAssignments != null || (board.drillDimensions?.length ?? 0) > 0;
  if (!hasDrillData) return board;
  const normalizedAssignments = board.drillAssignments
    ? Object.fromEntries(Object.entries(board.drillAssignments).map(([key, value]) => [key.toLowerCase(), value]))
    : board.drillAssignments;
  const normalizedDimensions = board.drillDimensions?.map((dimension) => ({
    ...dimension,
    id: dimension.id?.toLowerCase(),
    levels: dimension.levels?.map((level) => ({ ...level, id: level.id?.toLowerCase() })),
  }));
  return { ...board, drillAssignments: normalizedAssignments, drillDimensions: normalizedDimensions };
};

const levelIdsByDimension = (board: BoardOutputV1 | undefined): Map<string, Set<string>> =>
  new Map(
    (board?.drillDimensions ?? []).flatMap((dimension) =>
      dimension.id != null
        ? [[dimension.id, new Set((dimension.levels ?? []).flatMap((l) => (l.id != null ? [l.id] : [])))] as const]
        : [],
    ),
  );

/** Keep a drill step only while its level is still assigned to a board column; truncate each
 *  stack at the first invalid step. */
export const pruneDrillStateToBoard = (
  drillState: Readonly<Record<string, DrillStep[]>>,
  board: BoardOutputV1 | undefined,
): Record<string, DrillStep[]> => {
  const drillAssignments = board?.drillAssignments ?? {};
  const knownLevelIdsByDimension = levelIdsByDimension(board);
  const pruned: Record<string, DrillStep[]> = {};
  for (const [dimensionId, stack] of Object.entries(drillState)) {
    const knownLevels = knownLevelIdsByDimension.get(dimensionId);
    if (knownLevels) {
      const survivors: DrillStep[] = [];
      for (const step of stack) {
        if (!knownLevels.has(step.levelId) || drillAssignments[step.levelId] == null) break;
        survivors.push(step);
      }
      if (survivors.length > 0) pruned[dimensionId] = survivors;
    }
  }
  return pruned;
};

export const hydrateDrillDimensions = (board: BoardOutputV1 | undefined): HydratedDrillDimension[] => {
  const dimensions = board?.drillDimensions ?? [];
  const drillAssignments = board?.drillAssignments ?? {};
  return dimensions.flatMap((dimension) => {
    if (!dimension.id) return [];
    const levels: HydratedDrillLevel[] = sortLevelsByRank(dimension.levels)
      .filter((level): level is DrillDimensionLevelOutputV1 & { id: string } => level.id != null)
      .map((level) => {
        const columnName = drillAssignments[level.id] ?? null;
        return {
          id: level.id,
          name: level.name || columnName || `Level ${level.rank ?? 0}`,
          rank: level.rank ?? 0,
          columnName,
        };
      });
    return [
      {
        id: dimension.id,
        name: dimension.name ?? '',
        levels,
        assignedLevels: levels.filter((level) => level.columnName != null),
      },
    ];
  });
};

export const nextLevel = (
  dimension: HydratedDrillDimension,
  fromLevelId: string | undefined,
): (HydratedDrillLevel & { columnName: string }) | undefined => {
  const fromIndex = fromLevelId ? dimension.levels.findIndex((level) => level.id === fromLevelId) : -1;
  return dimension.levels
    .slice((fromIndex < 0 ? 0 : fromIndex) + 1)
    .find((level): level is HydratedDrillLevel & { columnName: string } => level.columnName != null);
};

/** The groupBy columns that sit on the x-axis: the fixed color-split (series identifier) is excluded,
 *  but only when there is another column to group on — a lone split column is itself the grouping. */
const xAxisGroupByColumns = (tileGroupBy: readonly string[], seriesIdentifier?: string): readonly string[] =>
  seriesIdentifier != null && tileGroupBy.some((column) => column !== seriesIdentifier)
    ? tileGroupBy.filter((column) => column !== seriesIdentifier)
    : tileGroupBy;

export const breadcrumbsForTile = (
  hydratedDimensions: HydratedDrillDimension[],
  drillState: Readonly<Record<string, DrillStep[]>>,
  tileGroupBy: readonly string[],
  seriesIdentifier?: string,
): TileBreadcrumb[] => {
  const xAxisColumns = new Set(xAxisGroupByColumns(tileGroupBy, seriesIdentifier));
  return hydratedDimensions.flatMap((dimension) => {
    const tileParticipates = dimension.assignedLevels.some(
      (level) => level.columnName != null && xAxisColumns.has(level.columnName),
    );
    if (!tileParticipates) return [];
    return [{ dimension, stack: drillState[dimension.id] ?? [] }];
  });
};

/** A tile participates in drilling if a dimension has an assigned level column on the tile's
 *  x-axis groupBy, excluding the fixed color-split column — drilled or not. */
export const tileParticipatesInDrill = (
  tile: { groupBy?: readonly string[] | null },
  drillState: Readonly<Record<string, DrillStep[]>>,
  hydratedDimensions: HydratedDrillDimension[],
  seriesIdentifier?: string,
): boolean =>
  breadcrumbsForTile(hydratedDimensions, drillState, Array.isArray(tile.groupBy) ? tile.groupBy : [], seriesIdentifier)
    .length > 0;

export type TileBreadcrumbLevels = {
  baseLevelId: string | null;
  currentLevelId: string | null;
  /** Clicking the base only clears the drill while it sits at or above the current level; when the
   *  drill is coarser than the base, selecting the base is a deeper switch that keeps the filters. */
  baseClearsDrill: boolean;
};

/** Maps columnName → assigned level for fast lookup. */
const assignedLevelsByColumnName = (
  assignedLevels: HydratedDrillLevel[],
): Map<string, HydratedDrillLevel & { columnName: string }> =>
  new Map(
    assignedLevels
      .filter((level): level is HydratedDrillLevel & { columnName: string } => level.columnName != null)
      .map((level) => [level.columnName, level]),
  );

/** Returns the first assigned level that matches an x-axis column, in tile axis order. */
const baseLevelForXAxis = (
  assignedLevels: HydratedDrillLevel[],
  xAxisColumns: readonly string[],
): (HydratedDrillLevel & { columnName: string }) | undefined => {
  const byColumnName = assignedLevelsByColumnName(assignedLevels);
  return xAxisColumns.map((col) => byColumnName.get(col)).find((level) => level != null);
};

/** Per-tile breadcrumb state: `baseLevelId` is the dimension's native x-axis grouping;
 *  `currentLevelId` is the top of the drill stack, or the base when not drilled. */
export const resolveTileBreadcrumbLevels = (
  dimension: HydratedDrillDimension,
  stack: DrillStep[],
  tileGroupBy: readonly string[],
  seriesIdentifier?: string,
): TileBreadcrumbLevels => {
  const xAxisColumns = xAxisGroupByColumns(tileGroupBy, seriesIdentifier);
  const baseLevelId = baseLevelForXAxis(dimension.assignedLevels, xAxisColumns)?.id ?? null;
  const currentLevelId = stack.at(-1)?.levelId ?? baseLevelId;
  const rankOf = (levelId: string | null) => dimension.assignedLevels.find((level) => level.id === levelId)?.rank;
  const baseRank = rankOf(baseLevelId);
  const currentRank = rankOf(currentLevelId);
  const baseClearsDrill = baseRank == null || currentRank == null || baseRank <= currentRank;
  return { baseLevelId, currentLevelId, baseClearsDrill };
};

/** The SELECT value picked on each column while drilling: a step's filter records the value that
 *  was selected on the previous level's column to enter it (see DrillStep.filter). */
export const selectedFilterValuesByColumnName = (stack: DrillStep[]): Map<string, FilterValue> => {
  const valuesByColumnName = new Map<string, FilterValue>();
  for (const step of stack) {
    if (step.filter != null && step.filter.filterType === FILTER_TYPE_SELECT && step.filter.values.length > 0) {
      valuesByColumnName.set(step.filter.columnName, step.filter.values[0]);
    }
  }
  return valuesByColumnName;
};
